"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { useReducedMotion } from "motion/react";
import type { GalleryItem } from "@shared/gallery";
import { cn } from "@/lib/utils";

const isVideo = (item: GalleryItem) => item.src.endsWith(".mp4");

/**
 * Grid of photos and videos. Videos play muted on a loop as soon as the page
 * loads (still for anyone with "reduce motion" on). Any tile opens a full-screen viewer with
 * its caption; arrows, arrow keys or a swipe move through the section.
 */
export function Gallery({ items, label }: { items: readonly GalleryItem[]; label: string }) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <>
      <ul aria-label={label} className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
        {items.map((item, i) => (
          <li key={item.src}>
            <button
              type="button"
              onClick={() => setOpen(i)}
              aria-label={`Open: ${item.caption}`}
              className="group relative block aspect-square w-full overflow-hidden rounded-2xl bg-surface"
            >
              <Tile item={item} />
              {isVideo(item) && (
                <span className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-full bg-ink/60 text-ink-foreground">
                  <Play className="size-4 fill-current" aria-hidden />
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>

      <Viewer items={items} index={open} onIndex={setOpen} />
    </>
  );
}

function Tile({ item }: { item: GalleryItem }) {
  const ref = useRef<HTMLVideoElement>(null);
  const reduceMotion = useReducedMotion();

  // `autoPlay` starts the video before the page's script loads; this stops it again
  // for "reduce motion" and nudges it on browsers that skipped the attribute.
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (reduceMotion) video.pause();
    else video.play().catch(() => {});
  }, [reduceMotion]);

  const media = "size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]";
  if (isVideo(item)) {
    return (
      <video
        ref={ref}
        src={item.src}
        poster={item.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden
        className={media}
      />
    );
  }
  return (
    <Image src={item.src} alt="" fill sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw" className={media} />
  );
}

function Viewer({
  items,
  index,
  onIndex,
}: {
  items: readonly GalleryItem[];
  index: number | null;
  onIndex: (i: number | null) => void;
}) {
  const touchX = useRef<number | null>(null);
  const go = useCallback(
    (step: number) => index !== null && onIndex((index + step + items.length) % items.length),
    [index, items.length, onIndex],
  );
  const item = index === null ? null : items[index];

  return (
    <DialogPrimitive.Root open={item !== null} onOpenChange={(o) => !o && onIndex(null)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/95 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") go(1);
            if (e.key === "ArrowLeft") go(-1);
          }}
          onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (touchX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
            touchX.current = null;
          }}
          className="fixed inset-0 z-50 flex flex-col text-ink-foreground outline-none"
        >
          <div className="flex items-center justify-between gap-4 px-4 py-3 md:px-6">
            <p className="text-sm text-ink-foreground/70">
              {index !== null && `${index + 1} / ${items.length}`}
            </p>
            <DialogPrimitive.Close
              aria-label="Close"
              className="flex size-11 items-center justify-center rounded-full hover:bg-ink-foreground/10"
            >
              <X className="size-6" aria-hidden />
            </DialogPrimitive.Close>
          </div>

          <div className="relative min-h-0 flex-1 px-4 md:px-20">
            {item &&
              (isVideo(item) ? (
                <video
                  key={item.src}
                  src={item.src}
                  poster={item.poster}
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label={item.alt}
                  className="size-full object-contain"
                />
              ) : (
                <Image
                  key={item.src}
                  src={item.src}
                  alt={item.alt}
                  fill
                  sizes="100vw"
                  className="object-contain px-4 md:px-20"
                />
              ))}
            <ViewerButton side="left" onClick={() => go(-1)} />
            <ViewerButton side="right" onClick={() => go(1)} />
          </div>

          <DialogPrimitive.Title className="px-4 pb-6 pt-4 text-center text-lg md:px-6">
            {item?.caption}
          </DialogPrimitive.Title>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ViewerButton({ side, onClick }: { side: "left" | "right"; onClick: () => void }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === "left" ? "Previous" : "Next"}
      className={cn(
        "absolute top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-ink-foreground/10 hover:bg-ink-foreground/20 md:size-12",
        side === "left" ? "left-4" : "right-4",
      )}
    >
      <Icon className="size-6" aria-hidden />
    </button>
  );
}
